package one.healthium.profile.ui.food.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import one.healthium.profile.ui.food.models.AmountUiModel
import one.healthium.profile.ui.food.models.FloatPart
import one.healthium.profile.ui.food.models.Units
import one.healthium.ui.compose.components.AppPicker
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_7_80
import androidx.compose.ui.tooling.preview.Preview
import kotlin.math.max

@Composable
fun AmountPicker(
    value: AmountUiModel,
    onValueChanged: (AmountUiModel) -> Unit,
) {
    var topDividerOffset by remember { mutableStateOf<Dp?>(null) }
    var bottomDividerOffset by remember { mutableStateOf<Dp?>(null) }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(24.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.Center,
        ) {
            if (value.unit.isFloating) {
                val integerItems = remember { (0..1000).toList() }
                val items = remember(integerItems) { integerItems.map { "$it" } }
                val integerPart = value.value.toInt()
                val floatingPart = FloatPart.valueOf(value.value - integerPart)

                AppPicker(
                    modifier = Modifier.weight(1f),
                    items = items,
                    onPositionChanged = {
                        onValueChanged(value.copy(integerPart = integerItems[it]))
                    },
                    startIndex = integerPart,
                    textModifier = Modifier.fillMaxWidth().padding(4.dp),
                    textAlign = TextAlign.Center,
                    onDrawTopDivider = { topDividerOffset = it },
                    onDrawBottomDivider = { bottomDividerOffset = it },
                )

                val floatingItems = remember { FloatPart.entries.map { it.displayValue } }
                AppPicker(
                    items = floatingItems,
                    onPositionChanged = { index ->
                        onValueChanged(value.copy(floatingPart = FloatPart.entries[index]))
                    },
                    startIndex = FloatPart.entries.indexOf(floatingPart),
                    textModifier = Modifier.defaultMinSize(minWidth = 100.dp).padding(4.dp),
                    textAlign = TextAlign.Center,
                )
            } else {
                val integerItems = remember { (10..1000 step 10).toList() }
                val items = remember(integerItems) { integerItems.map { "$it" } }
                val startIndex = remember(value, integerItems) { integerItems.indexOf(value.value.toInt()) }
                AppPicker(
                    modifier = Modifier.weight(1f),
                    items = items,
                    onPositionChanged = {
                        onValueChanged(value.copy(integerPart = integerItems[it], floatingPart = FloatPart.Zero))
                    },
                    startIndex = max(0, startIndex),
                    textModifier = Modifier.fillMaxWidth().padding(4.dp),
                    textAlign = TextAlign.Center,
                    onDrawTopDivider = { topDividerOffset = it },
                    onDrawBottomDivider = { bottomDividerOffset = it },
                )
            }
            AppPicker(
                modifier = Modifier.weight(1f),
                items = remember { value.units.map { it.displayValue } },
                onPositionChanged = {
                    onValueChanged(
                        value.copy(unit = value.units[it])
                    )
                },
                startIndex = max(0, value.units.indexOf(value.unit)),
                textModifier = Modifier.fillMaxWidth().padding(4.dp),
                textAlign = TextAlign.Center,
            )
        }

        if (topDividerOffset != null) {
            HorizontalDivider(
                modifier = Modifier.fillMaxWidth().offset(y = topDividerOffset!!),
                color = gray_7_80,
            )
        }
        bottomDividerOffset?.let {
            HorizontalDivider(
                modifier = Modifier.fillMaxWidth().offset(y = it),
                color = gray_7_80,
            )
        }
    }
}

@Composable
@Preview
private fun AmountPickerPreviewMetric() {
    AppTheme {
        Surface {
            AmountPicker(
                value = AmountUiModel(
                    integerPart = 100,
                    floatingPart = FloatPart.Zero,
                    unit = Units.Gram,
                    units = listOf(Units.Gram, Units.Ml),
                ),
                onValueChanged = {},
            )
        }
    }
}
